import { useMemo } from 'react';
import { formatMoney } from '../../lib';
import type { ContextCartItem } from '../types';

interface TileCartItemResult {
  isTile: boolean;
  squareMetersPerBox: number;
  meteredPrice: string | null;
  totalArea: string | null;
  formattedQuantity: string;
  areaDisplay: string | null;
}

export function useTileCartItem(item: ContextCartItem): TileCartItemResult {
  const isTile = item.category === 'TILE';

  // Use the actual squareMetersPerBox from cart item data
  const squareMetersPerBox = item.squareMetersPerBox ?? 1; // Default fallback to 1

  // Calculate per-square-meter price for tiles
  const meteredPrice = useMemo(() => {
    if (!isTile || !squareMetersPerBox || item.quantity === 0) return null;

    // First get price per box by dividing total price by quantity
    const pricePerBox = Number.parseFloat(item.price.amount) / item.quantity;

    // Then get price per square meter by dividing price per box by square meters per box
    const pricePerSquareMeter = {
      amount: Math.round(pricePerBox / squareMetersPerBox).toString(),
      currencyCode: item.price.currencyCode,
    };

    return `${formatMoney(pricePerSquareMeter)}/m²`;
  }, [isTile, squareMetersPerBox, item.price, item.quantity]);

  // Calculate total area for tiles
  const totalArea = useMemo(() => {
    if (!isTile || !squareMetersPerBox) return null;
    return (item.quantity * squareMetersPerBox).toFixed(2);
  }, [isTile, squareMetersPerBox, item.quantity]);

  // Format quantity with "Boxes" suffix for tiles
  const formattedQuantity = useMemo(() => {
    return `${item.quantity}${isTile ? ' Boxes' : ''}`;
  }, [item.quantity, isTile]);

  // Format area display for tiles
  const areaDisplay = useMemo(() => {
    if (!isTile || !totalArea) return null;
    return `= ${totalArea} m²`;
  }, [isTile, totalArea]);

  return {
    isTile,
    squareMetersPerBox,
    meteredPrice,
    totalArea,
    formattedQuantity,
    areaDisplay,
  };
}
